import { useId, useState, type ReactNode } from 'react';
import { cx } from '../ui/primitives';

/**
 * Chart pieces for the dashboard.
 *
 * Deliberately plain: every mark is inline SVG with no charting dependency, and
 * every chart carries **one** series. A single series needs no legend — the title
 * names it — and it sidesteps the whole categorical-colour problem, which is the
 * usual source of unreadable dashboards.
 *
 * The mark colour is `accent-strong` (#1481B2) rather than the app's accent: the
 * accent is 2.81:1 against white, below the 3:1 a data mark needs. Bars and lines
 * also carry **direct value labels**, which is what makes a low-contrast mark
 * legible regardless — the number is readable even if the fill is not.
 *
 * Grid and axes are recessive; the data is the only thing drawn at full strength.
 */

const MARK = '#1481B2';
const MARK_SOFT = '#CFE7F2';
const GRID = '#E5E7EB';

/**
 * People, written the way the briefs write them.
 *
 * Millions, because that is the unit every size in these records uses — showing
 * `31,200,000` where the brief said `30-35 mn` makes the reader do arithmetic to
 * check the figure they already knew.
 */
export function formatAudience(people: number): string {
  if (!people) return '0';
  if (people >= 1_000_000) {
    const millions = people / 1_000_000;
    // One decimal below ten, so 1.2 mn does not collapse to 1 mn.
    return `${millions < 10 ? millions.toFixed(1) : Math.round(millions)} mn`;
  }
  if (people >= 1_000) return `${Math.round(people / 1_000)} k`;
  return String(people);
}

/** A chart's frame: a title, an optional aside, and the plot. */
export function ChartCard({
  title,
  hint,
  aside,
  children,
}: {
  title: string;
  hint?: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-card border border-line bg-surface p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-section font-semibold text-ink">{title}</h3>
          {hint ? <p className="mt-0.5 text-micro text-ink-muted">{hint}</p> : null}
        </div>
        {aside}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

/**
 * A headline number.
 *
 * A stat tile rather than a one-bar chart: a single current value has no shape to
 * plot, and a bar of one is just a number wearing a rectangle.
 */
export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: 'neutral' | 'attention';
}) {
  return (
    <div className="rounded-card border border-line bg-surface p-4">
      <p className="text-micro font-medium uppercase tracking-wide text-ink-muted">{label}</p>
      <p
        className={cx(
          // Smaller on a phone, where four tiles share the width two at a time.
          'mt-1.5 text-2xl font-semibold leading-none sm:text-[1.75rem]',
          tone === 'attention' ? 'text-warning' : 'text-ink',
        )}
      >
        {value}
      </p>
      {hint ? <p className="mt-1.5 text-micro text-ink-muted">{hint}</p> : null}
    </div>
  );
}

/**
 * One ratio against its limit.
 *
 * A meter, not a two-slice pie: the reader's question is "how far along", which a
 * length answers directly. The track is a step of the same hue so the filled and
 * unfilled parts read as one scale.
 */
export function Meter({
  value,
  total,
  label,
}: {
  value: number;
  total: number;
  label: string;
}) {
  const share = total > 0 ? Math.min(1, value / total) : 0;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-micro text-ink-muted">{label}</span>
        <span className="text-field font-medium text-ink">
          {value} / {total}
        </span>
      </div>

      <div
        className="mt-2 h-2.5 w-full overflow-hidden rounded-full"
        style={{ backgroundColor: MARK_SOFT }}
        role="img"
        aria-label={`${label}: ${value} of ${total}`}
      >
        <div
          className="h-full rounded-full transition-[width]"
          style={{ width: `${share * 100}%`, backgroundColor: MARK }}
        />
      </div>
    </div>
  );
}

export interface BarDatum {
  label: string;
  value: number;
  /** Optional second line under the label, for context rather than a series. */
  sublabel?: string;
  /**
   * What to print as the direct label, when the raw value is not what a reader
   * wants to see — an audience of 31,200,000 reads better as `31 mn`.
   */
  display?: string;
}

/**
 * Horizontal bars for comparing magnitude.
 *
 * Horizontal because the labels are words — channel and brand names — and a
 * vertical column chart would either rotate them or truncate them. Each bar keeps
 * its value beside it, so the chart is readable without measuring against an axis.
 */
export function BarList({ data, valueSuffix = '' }: { data: BarDatum[]; valueSuffix?: string }) {
  if (!data.length) {
    return <p className="text-micro text-ink-muted">Nothing recorded yet.</p>;
  }

  // Scaled to the largest bar, so the comparison uses the full width available.
  const max = Math.max(...data.map((entry) => entry.value), 1);

  return (
    <ul className="space-y-3">
      {data.map((entry) => (
        <li key={entry.label}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-field text-ink" title={entry.label}>
              {entry.label}
              {entry.sublabel ? (
                <span className="ml-1.5 text-micro text-ink-faint">{entry.sublabel}</span>
              ) : null}
            </span>
            {/* The direct label: the number never has to be measured off an axis. */}
            <span className="shrink-0 text-field font-medium text-ink tabular-nums">
              {entry.display ?? entry.value}
              {entry.display ? '' : valueSuffix}
            </span>
          </div>

          <div className="mt-1.5 h-2 w-full rounded-full" style={{ backgroundColor: '#F0F1F3' }}>
            <div
              className="h-full rounded-full"
              style={{
                // Anchored to the baseline at 0 and never below a hairline, so a
                // value of 1 next to a value of 40 is still visibly present.
                width: `${Math.max(2, (entry.value / max) * 100)}%`,
                backgroundColor: MARK,
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

export interface TrendPoint {
  label: string;
  value: number;
}

/**
 * A single series over time, as an area with a line and markers.
 *
 * The months come from the calendar rather than from the data, so a quiet month
 * is a dip rather than a missing point — a chart that skips empty periods implies
 * activity that never happened.
 *
 * Hovering a point shows its value. An HTML chart is interactive by nature, and
 * without a tooltip the reader is left estimating against a gridline.
 */
export function TrendChart({ points, caption }: { points: TrendPoint[]; caption: string }) {
  const gradientId = useId();
  const [hovered, setHovered] = useState<number | null>(null);

  if (points.length < 2) {
    return <p className="text-micro text-ink-muted">Not enough history to show a trend yet.</p>;
  }

  const width = 640;
  const height = 180;
  const padding = { top: 14, right: 12, bottom: 26, left: 12 };

  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  // A floor of 1 keeps a flat run of zeroes on the baseline instead of dividing
  // by zero and drawing the line through the middle of the plot.
  const max = Math.max(...points.map((point) => point.value), 1);

  const xOf = (index: number) => padding.left + (index / (points.length - 1)) * plotWidth;
  const yOf = (value: number) => padding.top + plotHeight - (value / max) * plotHeight;

  const line = points.map((point, index) => `${xOf(index)},${yOf(point.value)}`).join(' ');
  const area = `${padding.left},${padding.top + plotHeight} ${line} ${padding.left + plotWidth},${padding.top + plotHeight}`;

  return (
    <div className="relative">
      {/*
        Scaled proportionally. Forcing the plot to a fixed pixel height while
        stretching it to the card's width distorted every mark — the point
        markers came out as ovals — and the distortion grew with the viewport,
        so it was worst on the widest screens.
      */}
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label={caption}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={MARK} stopOpacity="0.18" />
            <stop offset="100%" stopColor={MARK} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Recessive grid: the baseline and the top of the scale, nothing more. */}
        <line
          x1={padding.left}
          y1={padding.top + plotHeight}
          x2={padding.left + plotWidth}
          y2={padding.top + plotHeight}
          stroke={GRID}
          strokeWidth="1"
        />
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left + plotWidth}
          y2={padding.top}
          stroke={GRID}
          strokeWidth="1"
          strokeDasharray="3 4"
        />

        <polygon points={area} fill={`url(#${gradientId})`} />
        <polyline points={line} fill="none" stroke={MARK} strokeWidth="2" strokeLinejoin="round" />

        {points.map((point, index) => (
          <g key={point.label}>
            <circle
              cx={xOf(index)}
              cy={yOf(point.value)}
              r={hovered === index ? 5.5 : 4}
              fill={MARK}
              // A surface ring, so a marker over the line still reads as a point.
              stroke="#FFFFFF"
              strokeWidth="2"
            />
            {/*
              The hit target is the full column, not the 8px marker — a mark that
              small is nearly impossible to hover deliberately.
            */}
            <rect
              x={xOf(index) - plotWidth / (points.length - 1) / 2}
              y={padding.top}
              width={plotWidth / (points.length - 1)}
              height={plotHeight}
              fill="transparent"
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(null)}
            />
          </g>
        ))}
      </svg>

      <div className="mt-1 flex justify-between text-[0.6875rem] text-ink-faint sm:text-micro">
        {points.map((point) => (
          <span key={point.label}>{point.label}</span>
        ))}
      </div>

      {hovered !== null ? (
        <div className="pointer-events-none absolute left-0 top-0 w-full">
          <p className="text-center text-micro text-ink">
            <span className="font-medium">{points[hovered]!.label}</span>
            {' · '}
            {points[hovered]!.value} request{points[hovered]!.value === 1 ? '' : 's'}
          </p>
        </div>
      ) : null}
    </div>
  );
}
